import {
	AutoComplete,
	Checkbox,
	DatePicker,
	Input,
	SelectMasterInfo,
} from '@/components';
import { cn, getFieldDateProps } from '@/utils';
import { ForwardedRef, forwardRef, memo, useCallback } from 'react';

import { isNilOrEmpty } from 'ramda-adjunct';

import FilterDate from './filter-date';
import FilterTime from './filter-time';
import InputTargetMobile from './input-target.mobile';
import useLogic from './logic';
import {
	EEditableFilterType,
	ISearchFilter,
	ISearchFilterRef,
	resolveFilterItem,
	TRootOptions,
} from './type';
const SearchFilter = (
	props: ISearchFilter,
	ref: ForwardedRef<ISearchFilterRef>
) => {
	const { labelClassName, containerClassName } = props;
	const { formik, state, onSearch } = useLogic(props, ref);

	const renderItem = (item: TRootOptions) => {
		const { label, value, filterItem: fI } = item;
		const filterItem = resolveFilterItem(fI, formik.values);
		switch (filterItem?.type) {
			case EEditableFilterType.TEXT_INPUT:
				return (
					<Input
						label={label}
						labelClassName={labelClassName}
						inputClassName="!w-[376px]"
						{...filterItem.inputProps}
						name={value}
						value={formik.values[value]}
						onChange={(e) =>
							formik.setValues((prev: any) => ({
								...prev,
								[value]: filterItem.inputProps?.formatInput
									? filterItem.inputProps?.formatInput(
											e.target.value,
											filterItem.inputProps?.maxLength
										)
									: e.target.value,
							}))
						}
						onPressEnter={onSearch}
					/>
				);
			case EEditableFilterType.DROPDOWN:
				return (
					<SelectMasterInfo
						label={label}
						labelClassName={labelClassName}
						className="!w-full !h-[40px]"
						{...filterItem.dropdownProps}
						name={value}
						value={formik.values[value] ?? ''}
						onChange={(e, op) =>
							formik.setValues((prev: any) => ({
								...prev,
								[value]: e,
							}))
						}
						onSelect={onSearch}
					/>
				);
			case EEditableFilterType.DATE_RANGE_PICKER:
				return (
					<FilterDate
						label={label}
						labelClassName={labelClassName}
						name={value}
						formik={formik}
						onSearch={onSearch}
						{...filterItem.dateProps}
					/>
				);
			case EEditableFilterType.TIME_PICKER:
				return (
					<FilterTime
						label={label}
						labelClassName={labelClassName}
						name={value}
						formik={formik}
						onSearch={onSearch}
					/>
				);
			case EEditableFilterType.DATE:
				return (
					<DatePicker
						{...getFieldDateProps(formik, value, filterItem.dateProps?.option)}
						label={label}
						labelClassName={labelClassName}
						inputClassName="!w-[376px]"
					/>
				);
			case EEditableFilterType.CHECKBOX:
				return (
					<Checkbox
						label={label}
						labelClassName={labelClassName}
						name={value}
						checked={formik.values[value]}
						onChange={(e) =>
							formik.setValues((prev) => ({
								...prev,
								[value]: e.target.checked,
							}))
						}
						onClick={onSearch}
					/>
				);
			case EEditableFilterType.AUTOCOMPLETE:
				return (
					<AutoComplete
						labelClassName={labelClassName}
						label={label}
						inputClassName="!w-[376px]"
						{...filterItem?.autoCompleteProps}
						options={filterItem?.autoCompleteProps?.options}
						searchValue={formik.values[value]}
						value={formik.values[value]}
						onChange={(e) => {
							formik.setValues((prev) => ({
								...prev,
								[value]: e,
							}));
						}}
						onSelect={onSearch}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								onSearch();
							}
						}}
					/>
				);
			case EEditableFilterType.INPUT_TARGET:
				return (
					<InputTargetMobile
						label={label}
						labelClassName={labelClassName}
						name={value}
						formik={formik}
						inputProps={filterItem.inputProps}
						dropdownProps={filterItem.dropdownProps}
						autoCompleteProps={filterItem.autoCompleteProps}
						onSearch={onSearch}
						searchTypeConfigs={filterItem.searchTypeConfigs}
					/>
				);
			case EEditableFilterType.HIDDEN:
				return (
					<Input
						label={label}
						labelClassName={labelClassName}
						inputClassName="!w-[376px] border-gray6"
						name={value}
						value={formik.values[value]}
						onChange={(e) =>
							formik.setValues((prev: any) => ({
								...prev,
								[value]: e.target.value,
							}))
						}
						onPressEnter={onSearch}
						containerClassName="hidden"
					/>
				);
			default:
				return (
					<Input
						label={label}
						labelClassName={labelClassName}
						inputClassName="!w-[376px] border-gray6"
						name={value}
						value={formik.values[value]}
						onChange={(e) =>
							formik.setValues((prev: any) => ({
								...prev,
								[value]: e.target.value,
							}))
						}
						onPressEnter={onSearch}
					/>
				);
		}
	};

	const renderDefaultFilter = useCallback(() => {
		if (isNilOrEmpty(state.defaultOptions)) return null;

		return state.defaultOptions?.map((item) => {
			const children = renderItem(item);
			if (children) {
				return <div key={item.value}>{renderItem(item)}</div>;
			}
		});
	}, [state.defaultOptions, renderItem]);

	return (
		<div
			id="search-filter"
			className="sticky top-[52px] md:top-[62px] z-10 bg-white"
		>
			<div
				className={cn(
					'bg-white border-b-[8px] border-softPeach px-[18px] md:px-[52px] py-[20px] md:py-[18px]',
					containerClassName
				)}
			>
				<div className="col gap-y-2">{renderDefaultFilter()}</div>
			</div>
		</div>
	);
};

export default memo(forwardRef(SearchFilter));
